import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Grid, Stack, styled, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { getJobById, IJob } from 'wisestep-api';
import { WsColors, WsTypography, WsTypoVariants, WsTypoColors } from 'wisestep-base';

import WsAvatar from '../../teams/components/WsAvatar';
import WsLink from '../../teams/components/WsLink';
import { EmailDetails, ICommunicationLog } from '../types/CommunicationTypes';
import {
  defaultCommunicationLog,
  formatCommunicationDate,
  formatJobTitle,
  getCommStatusBadge,
} from '../utils/communicationUtils';

interface EmailViewerProps {
  emailLog: ICommunicationLog<EmailDetails>;
  jobInfo?: IJob;
  isLoading?: boolean;
}

const ContactInfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: WsColors.TertiaryShades50,
  padding: theme.spacing(1.5, 3.5),
  borderRadius: theme.spacing(0.875),
  margin: theme.spacing(2, 2, 0, 2),
}));

const InfoRow = styled(Stack)(() => ({
  alignItems: 'center',
  gap: 9,
}));

const SubjectContainer = styled(Box)(({ theme }) => ({
  backgroundColor: WsColors.White,
  padding: theme.spacing(2.25, 2),
  borderBottom: `1px solid ${WsColors.TertiaryShades200}`,
}));

const SenderInfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: WsColors.White,
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${WsColors.TertiaryShades200}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const EmailContentContainer = styled(Box)(() => ({
  backgroundColor: WsColors.White,
  padding: '13px 20px 13px 62px',
  height: 590,
  overflowY: 'auto',
}));

const InfoIcon = styled(Box)(() => ({
  width: 18,
  height: 18,
  color: WsColors.TertiaryShades800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const EmailViewer: FC<EmailViewerProps> = ({ emailLog, isLoading = false }) => {
  emailLog = emailLog ?? defaultCommunicationLog();
  const [job, setJob] = useState<IJob | null>(null);
  const emailDetails = emailLog.details as EmailDetails;

  useEffect(() => {
    if (!emailLog.jobId) return;
    const fetchJobData = async () => {
      const jobData: IJob = (await getJobById(emailLog.jobId)).data;
      jobData && setJob(jobData);
    };
    fetchJobData();
  }, [emailLog.jobId]);

  return (
    <Grid container direction="column" sx={{ height: '100%' }}>
      <Grid item>
        <ContactInfoContainer>
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 1.5 }}>
            <WsAvatar name={emailLog.candidateName} size="small" variant="circular" />
            <Box flex={1}>
              <WsTypography
                variant={WsTypoVariants.BODY1}
                color={WsTypoColors.PRIMARY}
                bold
                cursor="pointer"
              >
                To: {emailLog.candidateName}
              </WsTypography>
            </Box>
            {getCommStatusBadge(emailLog.status, emailLog.type)}
          </Stack>
          <Stack direction="row" alignItems="center" gap={4}>
            <InfoRow display="flex" direction="row">
              <InfoIcon>
                <WorkIcon sx={{ fontSize: 18 }} />
              </InfoIcon>
              <WsLink to={`/jobs-crm/${emailLog.jobId}/false/0/pipeline`}>
                <WsTypography
                  cursor="pointer"
                  variant={WsTypoVariants.BODY2}
                  color={WsTypoColors.PRIMARY750}
                  bold
                >
                  {formatJobTitle(job?.title || 'Unknown Job Title', job?.jobCode)}
                </WsTypography>
              </WsLink>
            </InfoRow>
            <InfoRow display="flex" direction="row">
              <InfoIcon>
                <BusinessIcon sx={{ fontSize: 18 }} />
              </InfoIcon>
              <WsLink
                to={`/client-details?name=${job?.companyName}&isChild=true&clientId=${
                  job?.clientId ?? ''
                }&jobCode=${job?.jobCode}&tabIndex=0`}
              >
                <WsTypography
                  cursor="pointer"
                  variant={WsTypoVariants.BODY2}
                  color={WsTypoColors.PRIMARY750}
                  bold
                >
                  {job?.companyName ?? 'Not Available'}
                </WsTypography>
              </WsLink>
            </InfoRow>
          </Stack>
        </ContactInfoContainer>
      </Grid>
      <Grid item>
        <SubjectContainer>
          <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
            {emailDetails.subject}
          </WsTypography>
        </SubjectContainer>
      </Grid>
      <Grid item>
        <SenderInfoContainer>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <WsAvatar name={emailLog.recruiterName} size="small" variant="circular" />
            <Box>
              <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.BLACK} bold>
                {emailLog.recruiterName}
              </WsTypography>
              <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.SENARY}>
                {`< ${emailLog.recruiterId} >`}
              </WsTypography>
            </Box>
          </Stack>
          <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.SENARY}>
            {formatCommunicationDate(emailLog.createdOn)}
          </WsTypography>
        </SenderInfoContainer>
      </Grid>
      <Grid item flex={1} sx={{ overflow: 'hidden' }}>
        <EmailContentContainer>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.QUATERNARY}>
                Loading email content...
              </WsTypography>
            </Box>
          ) : (
            <Box
              sx={{
                pb: 10,
                '& .email-content': {
                  whiteSpace: 'pre-line',
                  lineHeight: 1.43,
                },
              }}
            >
              <Typography
                className="email-content"
                variant="body2"
                color={WsTypoColors.BLACK}
                fontWeight={500}
                dangerouslySetInnerHTML={{
                  __html: emailDetails.body,
                }}
              />
            </Box>
          )}
        </EmailContentContainer>
      </Grid>
    </Grid>
  );
};

export default EmailViewer;
